import VueRouter, { Route } from 'vue-router';
import { Mixins } from 'vue-property-decorator';
import Vue, { VueConstructor } from 'vue';

import {
    getFeatureEnabled,
    isBreakpointMobileQuery,
    modalService,
} from '@gig/magic-common';
import type { IModal, IModalOptions } from '@gig/magic-common';

import { store } from '../../../store';
import { getModals, isModalRoute } from '../../../util';
import { isUserAllowed } from './authentication.guard';
import { i18nQuery } from '@gig/magic-frontend-toolkit';
import { CLEAR_MODALS, WINDOW_ROUTE_CHANGE } from '@gig/magic-messages';
import { getFeature } from './feature.guard';
import {
    isLocationAllowedForRoute,
    setupAllowedCountriesModal,
} from './allowed-countries.guard';
import { RouteName } from '../../../routes/routes.constants';

type ModalOptions =
    | IModalOptions
    | ((
          route: IModalRoute,
          router: VueRouter,
      ) => IModalOptions | Promise<IModalOptions>);

type ComponentType = () =>
    | VueConstructor
    | Promise<VueConstructor>
    | Promise<{ default: VueConstructor }>;

export interface IModalMeta {
    modal: {
        title: string | ((route: IModalRoute) => string) | null;
        component: ComponentType;
        uniqueModalId: string;
        propData?: Record<string, any>;
        options?: ModalOptions;
        desktopOnly?: boolean | ((route: Route) => boolean);
    };
}

export interface IModalRoute extends Route {
    meta: IModalMeta;
}

/**
 * Guard to open modals on specific routes.
 * @param component Component instance to open
 * @param title Translatable title of the modal
 * @param options Further options to pass to the modal
 * @param options.uniqueModalId Modal ID used to group modals that should override each other.
 * @param options.propData Props to pass through to the component
 */
export function modalGuard(
    component: ComponentType,
    title: IModalMeta['modal']['title'],
    {
        uniqueModalId = 'default',
        propData,
        options,
        desktopOnly = true,
    }: {
        uniqueModalId?: string;
        propData?: Record<string, any>;
        options?: ModalOptions;
        desktopOnly?: IModalMeta['modal']['desktopOnly'];
    } = {},
): IModalMeta {
    return {
        modal: {
            title,
            component,
            uniqueModalId,
            propData,
            options,
            desktopOnly,
        },
    };
}

/**
 * Returns an array of paths for the routes specified.
 * @param routes
 */
function getPathArray(routes: IModalRoute[]): string[] {
    return Object.values(
        routes.reduce<Record<string, string>>((prev, curr) => {
            prev[curr.meta.modal.uniqueModalId] = curr.fullPath;
            return prev;
        }, {}),
    );
}

/**
 * Removes the modal route specified from the current URLs
 * query parameters.
 * @param router Router instance
 * @param route The route to remove from the current path
 */
function navigateAwayFromModal(router: VueRouter, route: IModalRoute) {
    router.push({
        ...router.currentRoute,
        name: router.currentRoute.name || undefined,
        query: {
            ...router.currentRoute.query,
            modal: getPathArray(
                getModals(router.currentRoute.query, router).filter(
                    ({ meta }) => {
                        return (
                            meta.modal.uniqueModalId !==
                            route.meta.modal.uniqueModalId
                        );
                    },
                ),
            ),
        },
    });
}

type ComponentWithInstance = InstanceType<VueConstructor> & {
    $_instance: VueConstructor;
    beforeModalClose?: (closeButton: boolean, params?: any) => void;
};

/**
 * Instantiates a component and returns the instance.
 * @param Component The component to instantiate
 * @param router The main vue router instance
 */
async function createComponent(
    Component: VueConstructor,
    route: IModalRoute,
    router: VueRouter,
): Promise<ComponentWithInstance> {
    // Create a component clone with route information.
    const ComponentWithRoute = class extends Mixins(Component) {
        $_instance = Component;
        get $route() {
            return this.$data.$route;
        }
    };

    const comp = new ComponentWithRoute({
        i18n: await i18nQuery(),
        store,
        router,
        propsData: route.meta.modal.propData,
        data: { $route: route },
    });

    return comp.$mount();
}

/**
 * Returns the title translation key for this modal route.
 * @param title The title function or string
 * @param route Route to pass to the title function
 */
function getTitle(
    title: IModalMeta['modal']['title'],
    route: IModalRoute,
): string | null {
    if (!title) {
        return null;
    }

    if (typeof title === 'string') {
        return title;
    }

    return title(route);
}

/**
 * Returns the title translation key for this modal route.
 * @param title The title function or string
 * @param route Route to pass to the title function
 */
function getDesktopOnly(
    desktopOnly: IModalMeta['modal']['desktopOnly'],
    route: Route,
): boolean {
    if (!desktopOnly) {
        return false;
    }

    if (typeof desktopOnly === 'boolean') {
        return desktopOnly;
    }

    return desktopOnly(route);
}

function isModule(
    component: unknown,
): component is { default: VueConstructor } {
    return (component as any).default;
}

async function resolveComponent(component: ComponentType) {
    const comp = await component();

    if (isModule(comp)) {
        return comp.default;
    }

    return comp;
}

export function initModalGuard(router: VueRouter) {
    const modals = new Map<
        string,
        { modalRef: IModal; componentInstance: ComponentWithInstance }
    >();
    let allowRouteChange = false;

    // Redirect to modal based routes
    router.beforeResolve(async (to, from, next) => {
        const isMobile = await isBreakpointMobileQuery();
        if (!isModalRoute(to)) {
            if (from.path !== to.path) {
                // if `from.name` is empty and `to.name` points to homepage
                // we're in the initial navigation, in this case we don't want
                // to clear modals, since it interferes with users blocks etc
                if (isMobile && (from.name || to.name !== RouteName.HOME)) {
                    window.commandBus.publish(CLEAR_MODALS);
                }
                window.eventBus.publish(WINDOW_ROUTE_CHANGE, to.fullPath);
            }

            next();
            return;
        }

        const { desktopOnly } = to.meta.modal;

        if (getDesktopOnly(desktopOnly, from) && isMobile) {
            window.commandBus.publish(CLEAR_MODALS);
            window.eventBus.publish(WINDOW_ROUTE_CHANGE, to.fullPath);
            next();
            return;
        }

        const currentModals = getModals(from.query, router);

        if (currentModals.indexOf(to) !== -1) {
            next(false);
            return;
        }

        const dest = isUserAllowed(from)
            ? { ...from, name: from.name || undefined }
            : {
                  path: '/',
              };

        const fixedModals = currentModals.filter((modal) => !!modal.meta.modal);
        next({
            ...dest,
            query: {
                ...to.query,
                modal: getPathArray(fixedModals.concat(to)),
            },
        });
    });

    // After navigation has been confirmed synchronize modals in URL with modal service
    router.afterEach(async (to) => {
        const modalRoutes = getModals(to.query, router);
        allowRouteChange = false;

        for (const [key, openedModal] of modals.entries()) {
            const hasModal = modalRoutes.some(({ meta }) => {
                return !meta ? false : meta.modal.uniqueModalId === key;
            });

            if (!hasModal) {
                modalService.close(openedModal.modalRef, false);
            }
        }

        for (const route of modalRoutes) {
            const meta = route.meta;

            if (!meta.modal) {
                continue;
            }

            if (!(await isRouteAccessAllowed(route, router))) {
                continue;
            }

            const { uniqueModalId, component, title, options } = meta.modal;

            const Component = await resolveComponent(component);

            let componentInstance: ComponentWithInstance;
            let modalRef: IModal | null = null;

            if (!modals.has(uniqueModalId)) {
                componentInstance = await createComponent(
                    Component,
                    route,
                    router,
                );
            } else {
                const mapping = modals.get(uniqueModalId)!;

                componentInstance = mapping.componentInstance;
                modalRef = mapping.modalRef;

                if (
                    !(mapping.componentInstance.$_instance === Component) &&
                    modalRef
                ) {
                    componentInstance.$destroy();
                    if (componentInstance.beforeModalClose) {
                        componentInstance.beforeModalClose(false);
                    }
                    mapping.componentInstance = componentInstance =
                        await createComponent(Component, route, router);

                    // Replace the modal with the newly updated component
                    modalService.replace(modalRef, componentInstance.$el);
                } else if (
                    route.fullPath === componentInstance.$route.fullPath
                ) {
                    // Nothing has changed or needing to be updated for this modal route
                    continue;
                }

                // Update component route.
                componentInstance.$data.$route = { ...route };
            }

            const modalTitle = getTitle(title, route);
            const modalOptions =
                typeof options === 'function'
                    ? await options(route, router)
                    : { ...options };

            if (!modalRef) {
                modalRef = modalService.open(
                    modalTitle,
                    componentInstance.$el,
                    {
                        ...modalOptions,
                        onClose: (closeButton, params) => {
                            const componentInstance =
                                modals.get(uniqueModalId)!.componentInstance;
                            if (componentInstance.beforeModalClose) {
                                componentInstance.beforeModalClose(
                                    closeButton,
                                    params,
                                );
                            }
                            componentInstance.$destroy();
                            modals.delete(uniqueModalId);

                            if (allowRouteChange) {
                                navigateAwayFromModal(router, route);
                            }
                        },
                    },
                );
            } else {
                Vue.set(modalRef, 'header', modalTitle);
                Vue.set(modalRef, 'options', {
                    ...modalRef.options,
                    ...modalOptions,
                });
            }

            modals.set(uniqueModalId, { componentInstance, modalRef });
        }

        allowRouteChange = true;
    });
}

async function isRouteAccessAllowed(
    to: Route,
    router: VueRouter,
): Promise<boolean> {
    const featureSlug = getFeature(to);
    if (featureSlug) {
        const isEnabled = await getFeatureEnabled(featureSlug);
        if (!isEnabled) {
            router.push(RouteName.PAGE404);
            return false;
        }
    }

    if (!isUserAllowed(to)) {
        router.push({
            name: RouteName.LOGIN,
            query: {
                backTo: to.fullPath,
            },
        });
        return false;
    }

    if (!(await isLocationAllowedForRoute(to))) {
        setupAllowedCountriesModal();
        return false;
    }

    return true;
}
